import { INSPECTION_BANNER_STORE_NAME } from '@/constants/constants';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Dedicated, self-contained store for the work-progress SPLIT view's failure
 * warning. It holds only dismiss-state; whether the warning should show at all is
 * derived reactively from the polled inspection list (see
 * useInspectionWarningBanner).
 *
 * Persisted so a dismissal survives poll ticks and page reloads, and wiped on
 * operator switch via clearClientStores — these are shared multi-operator
 * workstations, so one operator's dismissal must not carry over to the next.
 */
interface InspectionBannerState {
  /**
   * _ids of the triggering inspections that were present when the operator last
   * dismissed the warning. The warning reappears only when a triggering _id that
   * is NOT in this list shows up — so an unchanged payload (same inspections, same
   * states) never re-triggers it, however many times it is polled.
   */
  dismissedIds: string[];
  // Persisted state rehydrates asynchronously. Consumers must wait for this or an
  // already-dismissed warning renders its panel for one frame on every reload.
  isStoreHydrated: boolean;
}

type Actions = {
  /**
   * Replace (not union) with the currently-triggering ids: newer inspections push
   * older triggering ids out of the latest-N window, so the set stays bounded
   * instead of growing all shift.
   */
  dismiss: (triggeringIds: string[]) => void;
  setIsStoreHydrated: (isHydrated: boolean) => void;
  resetStore: () => void;
};

export const useInspectionBannerStore = create<
  InspectionBannerState & Actions
>()(
  persist(
    (set) => ({
      dismissedIds: [],
      isStoreHydrated: false,
      dismiss: (triggeringIds: string[]) => {
        set({ dismissedIds: triggeringIds });
      },
      setIsStoreHydrated: (isHydrated: boolean) => {
        set({ isStoreHydrated: isHydrated });
      },
      resetStore: () => {
        set({ dismissedIds: [] });
      }
    }),
    {
      name: INSPECTION_BANNER_STORE_NAME,
      // Hydration flag is runtime-only; persisting it would rehydrate as `true`
      // before rehydration has actually happened.
      partialize: (state) => ({ dismissedIds: state.dismissedIds }),
      onRehydrateStorage: () => (state) => {
        state?.setIsStoreHydrated(true);
      }
    }
  )
);
